import ajax from "./ajax";

export function requestOrders(query) {
  return ajax("/orders/by-status-tags", { query });
}

export function requestOrderDetails(id, query) {
  return ajax(`/orders/${id}`, { query });
}

/** Color / size / personalization for draft-order list hover. */
export function requestOrderItemsPreview(id) {
  return ajax(`/orders/${id}`, { query: { itemsPreview: true } });
}

export function requestOrdersCount(query) {
  return ajax(`orders/counts`, { query });
}
export function requestItemsHistory(id, query) {
  return ajax(`/orders/${id}/itemsHistory`, { query });
}

export function requestOrderActivity(id, query) {
  return ajax(`/orders/${id}/activity`, { query });
}

export function requestOrderItemLocations(id, query) {
  return ajax(`/orders/${id}/item-locations`, { query });
}

export const syncOrders = (query) => {
  return ajax(`/orders/sync`, { server: "python", query });
};

export const syncParticularOrder = (query, options = {}) => {
  return ajax(`/v2/orders/sync`, {
    method: "PUT",
    server: "python",
    query,
    ...(options.data ? { data: options.data } : {}),
    ...(options.timeout != null ? { timeout: options.timeout } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });
};

/** Admin Refresh review: what the store changed on this order. Writes nothing. */
export const previewOrderStoreChanges = (query, options = {}) => {
  return ajax(`/v2/orders/sync`, {
    method: "PUT",
    server: "python",
    query: { ...query, preview: true },
    ...(options.timeout != null ? { timeout: options.timeout } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });
};

/** Put an ignored item back on the order. */
export const unignoreOrderItem = (orderId, itemId) => {
  return ajax(`/v2/orders/${orderId}/items/${itemId}/unignore`, {
    method: "POST",
    server: "python",
  });
};

export const requestDraftOrders = (query) => {
  return ajax("/orders", { query });
};

export const requestAdminDraftOrders = (query) => {
  return ajax("/orders/pending-orders", { query });
};

export const requestUnsyncedOrders = (query) => {
  return ajax("/orders", { query });
};

export const updateOrder = (id, data) => {
  return ajax(`/orders/${id}`, { method: "PUT", data });
};

/** Same note on many orders. One admin email for a seller's bulk note. */
export const addOrderNotesBulk = (data) => {
  return ajax(`/orders/bulk/notes`, { method: "POST", data, timeout: 120000 });
};

export const addOrderNote = (id, data) => {
  return ajax(`/orders/${id}/notes`, { method: "POST", data });
};

export const deleteOrderNote = (id, noteId) => {
  return ajax(`/orders/${id}/notes/${encodeURIComponent(noteId)}`, {
    method: "DELETE",
  });
};

export const uploadOrderNotesCsv = (data) => {
  return ajax(`/orders/notes/csv`, { method: "POST", data });
};

export const uploadOrderTrackingCsv = (data) => {
  return ajax(`/v2/orders/tracking/csv`, {
    method: "POST",
    data,
    server: "python",
    timeout: 180000,
  });
};

export const deleteOrder = (id, data = {}) => {
  return ajax(`/orders/${id}`, { method: "DELETE", data });
};

export const updateDraftOrder = (id, data) => {
  return ajax(`/orders/${id}/draft`, { method: "PUT", data });
};

export const splitDraftOrderItem = (id, data) => {
  return ajax(`/orders/${id}/draft/split-item`, { method: "POST", data });
};

export const updateAdminDraftOrder = (id, data) => {
  return ajax(`/orders/${id}/print-files`, { method: "PUT", data });
};

export const saveCsvOrder = (data) => {
  return ajax(`/orders/new/csv`, { method: "POST", data, server: "python" });
};

export const saveManualOrder = (data) => {
  return ajax(`/orders/new`, { method: "POST", data, server: "python" });
};

export const getOrderWeight = (id) => {
  return ajax(`/orders/${id}/weights`);
};

export const getOrderItemDetails = (id) => {
  return ajax(`/orders/items/${id}`);
};

export const redoOrder = (id, data) => {
  return ajax(`/orders/${id}/redo`, { method: "POST", data });
};

/** Preview money delta for an order-native change order (no writes). */
export const previewOrderChangeOrder = (id, data) => {
  return ajax(`/orders/${id}/change-order`, {
    method: "POST",
    data: { ...data, dryRun: true, confirm: false },
  });
};

/** Apply an order-native change order (items/shipping + Stripe refund/charge). */
export const applyOrderChangeOrder = (id, data) => {
  return ajax(`/orders/${id}/change-order`, {
    method: "POST",
    data: { ...data, confirm: true, dryRun: false },
  });
};

/** Charge and/or email the customer after a Riverr order edit. */
export const followUpOrderEdit = (id, data) => {
  return ajax(`/orders/${id}/edit-followup`, { method: "POST", data });
};

/** Add / remove line items before production. Shopify orders go through order edit + refresh. */
export const editOrderItems = (id, data) => {
  return ajax(`/v2/orders/${id}/edit`, {
    method: "POST",
    data,
    server: "python",
    timeout: 120000,
  });
};

/**
 * Unassign a franchise order's location so HQ can re-route it. Rejected once
 * the order has shipped, because the payment split has already run by then.
 */
export const sendOrderBackToHq = (id, data) => {
  return ajax(`/orders/${id}/send-back-to-hq`, { method: "POST", data });
};

/** Drop on-hold / high-risk on one order so production can continue. */
export const releaseOrderHold = (id) => {
  return ajax(`/orders/${id}/release-hold`, { method: "POST" });
};

/** Send a Shopify payment-hold order to production and record who released it. */
/** Release one order from pre-sale; the products stay on pre-sale. */
export const releaseOrderPresale = (id) => {
  return ajax(`/orders/${id}/release-presale`, { method: "POST" });
};

export const releasePaymentHold = (id) => {
  return ajax(`/orders/${id}/release-payment-hold`, { method: "POST" });
};

/**
 * PLC.1 — list of orders waiting for the print area to be placed on a
 * blank. Admin only; self clearing when the state resolves on its own.
 */
export const requestPlacementAttention = () => {
  return ajax(`/orders/placement-attention`);
};

/**
 * Re evaluate after the print area has been placed. Clears the needs
 * placement state when every line is covered and fires the proof render
 * hook on the server. Keeps the order waiting if a line is still missing.
 */
export const resolveOrderPlacement = (id) => {
  return ajax(`/orders/${id}/placement-done`, { method: "POST" });
};

/** Assign/re-assign a franchise order's fulfillment location. */
export const assignOrderLocation = (id, locationId) => {
  return ajax(`/orders/${id}/location`, {
    method: "POST",
    data: { locationId },
  });
};

export const setOrderLabels = (id, data) => {
  return ajax(`/orders/${id}/labels`, { method: "POST", data });
};

export const getPriceBreakdown = (id) => {
  return ajax(`/orders/${id}/cost`);
};

export const deleteOrderItem = (id, data) => {
  return ajax(`/orders/order-items/${id}`, {
    method: "DELETE",
    ...(data ? { data } : {}),
  });
};

export const getAutoDigitizedFiles = (id) => {
  return ajax(`/orders/order-items/${id}/add-auto-digitized-files`);
};

export const addAutoDigitizedFiles = (id, data) => {
  return ajax(`/orders/order-items/${id}/add-auto-digitized-files`, {
    method: "POST",
    data,
  });
};

export const updateAutoDigitizedFileStatus = (id, fileId, data) => {
  return ajax(`/orders/order-items/${id}/auto-digitized-files/${fileId}`, {
    method: "PATCH",
    data,
  });
};

export const getEmbroideryFileRequests = (orderItemId) => {
  return ajax(`/orders/order-items/${orderItemId}/embroidery-file-requests`);
};

export const createEmbroideryFileRequest = (orderItemId, data) => {
  return ajax(`/orders/order-items/${orderItemId}/embroidery-file-requests`, {
    method: "POST",
    data,
  });
};

export const uploadEmbroideryFileRequest = (orderItemId, requestId, data) => {
  return ajax(
    `/orders/order-items/${orderItemId}/embroidery-file-requests/${requestId}/upload`,
    { method: "POST", data }
  );
};

export const reviewEmbroideryFileRequest = (orderItemId, requestId, data) => {
  return ajax(
    `/orders/order-items/${orderItemId}/embroidery-file-requests/${requestId}/review`,
    { method: "POST", data }
  );
};

export const getEmbroideryFileRequestMessages = (orderItemId, requestId) => {
  return ajax(
    `/orders/order-items/${orderItemId}/embroidery-file-requests/${requestId}/messages`
  );
};

export const sendEmbroideryFileRequestMessage = (
  orderItemId,
  requestId,
  data
) => {
  return ajax(
    `/orders/order-items/${orderItemId}/embroidery-file-requests/${requestId}/messages`,
    { method: "POST", data }
  );
};

export const markShipped = (id, data) => {
  return ajax(`/v2/orders/${id}/manual-shipment`, {
    method: "POST",
    data,
    server: "python",
  });
};

/** Tracking for the lines selected in split ship. Does not close the rest. */
export const markShippedItems = (id, data) => {
  return ajax(`/v2/orders/${id}/manual-shipment-items`, {
    method: "POST",
    data,
    server: "python",
  });
};

/** Re-push tracking to Shopify/Etsy for an already-shipped order. */
export const submitShipmentToStore = (id, data) => {
  return ajax(`/v2/orders/${id}/submit-shipment`, {
    method: "POST",
    data: { orderId: id, ...data },
    server: "python",
  });
};

export const getShopifyFulfillmentFailures = (query = {}) => {
  return ajax(`/v2/shopify/fulfillment-failures`, {
    query,
    server: "python",
  });
};

export const retryShopifyFulfillmentFailures = (orderIds) => {
  return ajax(`/v2/shopify/fulfillment-failures/retry`, {
    method: "POST",
    data: { orderIds },
    server: "python",
  });
};

export const archiveShopifyFulfillmentFailures = (
  orderIds,
  status = "archived"
) => {
  return ajax(`/v2/shopify/fulfillment-failures/archive`, {
    method: "POST",
    data: { orderIds, status },
    server: "python",
  });
};

export const markReadyForPickup = (id) => {
  return ajax(`/v2/orders/${id}/ready-for-pickup`, {
    method: "POST",
    server: "python",
  });
};

export const getLateOrders = (query) => {
  return ajax(`/orders/late-orders`, { query });
};

export const getPrintFileAttention = () => {
  return ajax(`/orders/print-file-attention`);
};

export const getSplitShipPendingOrders = (query) => {
  return ajax(`/orders/split-ship-pending`, { query });
};

export const markEmbroideryPlacement = (orderId, data) => {
  return ajax(`/orders/${orderId}/embroidery-station`, {
    method: "POST",
    data,
  });
};
